import type {
  LisFlagRule,
  LisResultMappingRow,
  LisResultMappingRowBody,
  LisResultSourceType,
  LisResultValueType,
} from '@/api/lisResultsApi'

/**
 * Outgoing result mappings (z8nrz7ffzv): the rules the Outgoing Mapping form
 * checks before saving.
 *
 * They mirror the backend's `lis-result-mapping.ts`, so a mistake is shown
 * beside the field instead of coming back as a 400. The backend still decides.
 */

export const RESULT_CODE_PATTERN = /^[A-Za-z0-9]{1,20}$/
export const TEXT_MAX = 60

const NUMERIC = /^[+-]?(\d+(\.\d*)?|\.\d+)$/
const RANGE = /^\s*([+-]?\d+(?:\.\d+)?)\s*-\s*([+-]?\d+(?:\.\d+)?)\s*$/

export const VALUE_TYPE_OPTIONS: { value: LisResultValueType; label: string }[] = [
  { value: 'NM', label: 'NM — number' },
  { value: 'ST', label: 'ST — text' },
  { value: 'TX', label: 'TX — long text' },
]

export const FLAG_RULE_OPTIONS: { value: LisFlagRule; label: string; short: string }[] = [
  { value: 'none', label: 'None', short: '—' },
  { value: 'computed', label: 'Computed from range (L / H / NORMAL)', short: 'From range' },
  { value: 'entered', label: 'Entered by the tech at release', short: 'Entered' },
]

/** OBX-8, MedStar's list. */
export const ABNORMAL_FLAGS = ['NORMAL', 'A', 'AA', 'WATCH', 'L', 'LL', 'H', 'HH'] as const
export type AbnormalFlag = (typeof ABNORMAL_FLAGS)[number]

/** A row as typed into the add / edit form. */
export interface ResultRowDraft {
  sourceType: LisResultSourceType
  sourceField: string
  code: string
  text: string
  valueType: LisResultValueType
  units: string
  referenceRange: string
  flagRule: LisFlagRule
  required: boolean
}

export function emptyRowDraft(): ResultRowDraft {
  return {
    sourceType: 'form',
    sourceField: '',
    code: '',
    text: '',
    valueType: 'NM',
    units: '',
    referenceRange: '',
    flagRule: 'none',
    required: false,
  }
}

export function rowDraftFrom(row: LisResultMappingRow): ResultRowDraft {
  return {
    sourceType: row.sourceType,
    sourceField: row.sourceField,
    code: row.code,
    text: row.text ?? '',
    valueType: row.valueType,
    units: row.units ?? '',
    referenceRange: row.referenceRange ?? '',
    flagRule: row.flagRule,
    required: row.required ?? false,
  }
}

export function parseRange(range: string | null | undefined): { low: number; high: number } | null {
  const m = RANGE.exec(range ?? '')
  return m ? { low: Number(m[1]), high: Number(m[2]) } : null
}

export type RowDraftErrors = Partial<Record<'sourceField' | 'code' | 'units' | 'referenceRange' | 'flagRule', string>>

/**
 * Inline messages for the row form. Empty when it can be saved.
 * `otherCodes` are the mapping's other rows' codes (the edited row left out).
 */
export function rowDraftErrors(draft: ResultRowDraft, otherCodes: readonly string[]): RowDraftErrors {
  const errors: RowDraftErrors = {}
  if (!draft.sourceField) errors.sourceField = 'Choose the field the value comes from.'

  const code = draft.code.trim()
  if (!RESULT_CODE_PATTERN.test(code)) errors.code = '1–20 letters or digits, as built in Epic.'
  else if (otherCodes.includes(code)) errors.code = 'This test already sends that code.'

  if (draft.units.trim().length > TEXT_MAX) errors.units = `At most ${TEXT_MAX} characters.`
  if (draft.referenceRange.trim().length > TEXT_MAX) errors.referenceRange = `At most ${TEXT_MAX} characters.`

  if (draft.flagRule === 'computed') {
    if (draft.valueType !== 'NM') errors.flagRule = 'A flag can be computed only for a number (type NM).'
    else if (!draft.referenceRange.trim()) errors.flagRule = 'Computing a flag needs a reference range.'
    else if (!parseRange(draft.referenceRange)) errors.referenceRange = 'Use low-high, e.g. 60-85, to compute a flag.'
  }
  return errors
}

/** The row as the API takes it; blank optional text is sent as null to clear it. */
export function rowDraftBody(draft: ResultRowDraft): LisResultMappingRowBody {
  const text = (v: string) => v.trim() || null
  return {
    sourceType: draft.sourceType,
    sourceField: draft.sourceField,
    code: draft.code.trim(),
    text: text(draft.text),
    valueType: draft.valueType,
    units: text(draft.units),
    referenceRange: text(draft.referenceRange),
    flagRule: draft.flagRule,
    required: draft.required,
  }
}

/** Below the range L, above H, otherwise NORMAL. Null when it cannot be computed. */
export function computeFlag(value: string, range: string | null | undefined): AbnormalFlag | null {
  const r = parseRange(range)
  if (!r || !NUMERIC.test(value.trim())) return null
  const v = Number(value)
  return v < r.low ? 'L' : v > r.high ? 'H' : 'NORMAL'
}

export function flagRuleLabel(rule: LisFlagRule): string {
  return FLAG_RULE_OPTIONS.find((o) => o.value === rule)?.short ?? rule
}

/** The rows' ids with one moved up (-1) or down (+1). Unchanged at either end. */
export function moveRow(ids: readonly string[], id: string, by: -1 | 1): string[] {
  const out = [...ids]
  const i = out.indexOf(id)
  const j = i + by
  if (i < 0 || j < 0 || j >= out.length) return out
  ;[out[i], out[j]] = [out[j], out[i]]
  return out
}
